import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/api";

export default function Cart() {
    const {
        items,
        updateQty,
        removeItem,
        subtotal,
        shipping,
        total,
    } = useCart();
    const nav = useNavigate();

    return (
        <div
            className="max-w-[1200px] mx-auto px-6 md:px-12 pt-16 pb-32"
            data-testid="cart-page"
        >
            <div className="overline mb-4">Your selection</div>
            <h1
                className="font-serif text-5xl sm:text-6xl tracking-tight mb-12"
                style={{ color: "var(--vl-text)" }}
            >
                Your{" "}
                <em
                    style={{
                        color: "var(--vl-rose)",
                        fontStyle: "italic",
                    }}
                >
                    bag.
                </em>
            </h1>

            {items.length === 0 ? (
                <div className="py-16" data-testid="cart-page-empty">
                    <p
                        className="font-serif text-2xl"
                        style={{ color: "var(--vl-text)" }}
                    >
                        Your bag is empty.
                    </p>
                    <Link
                        to="/shop"
                        className="vl-btn vl-btn-solid mt-8"
                        data-testid="cart-empty-browse"
                    >
                        Browse the collection
                    </Link>
                </div>
            ) : (
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <div className="hairline mb-6" />
                        {items.map((i) => (
                            <div
                                key={i.product_id}
                                className="flex gap-5 py-6"
                                style={{
                                    borderBottom:
                                        "1px solid var(--vl-border)",
                                }}
                                data-testid={`cart-page-item-${i.slug}`}
                            >
                                <img
                                    src={i.image}
                                    alt={i.name}
                                    className="w-28 h-36 object-cover"
                                />
                                <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                        <Link
                                            to={`/product/${i.slug}`}
                                            className="font-serif text-2xl leading-tight"
                                            style={{
                                                color: "var(--vl-text)",
                                            }}
                                        >
                                            {i.name}
                                        </Link>
                                        <button
                                            onClick={() =>
                                                removeItem(i.product_id)
                                            }
                                            data-testid={`cart-page-remove-${i.slug}`}
                                            aria-label="Remove"
                                        >
                                            <X
                                                size={16}
                                                strokeWidth={1.25}
                                                color="var(--vl-muted)"
                                            />
                                        </button>
                                    </div>
                                    <div
                                        className="text-sm mt-2"
                                        style={{ color: "var(--vl-muted)" }}
                                    >
                                        {formatINR(i.price)}
                                    </div>
                                    <div className="flex items-center gap-3 mt-6">
                                        <button
                                            onClick={() =>
                                                updateQty(
                                                    i.product_id,
                                                    i.quantity - 1,
                                                )
                                            }
                                            className="w-8 h-8 border flex items-center justify-center"
                                            style={{
                                                borderColor:
                                                    "var(--vl-border)",
                                            }}
                                        >
                                            <Minus size={12} />
                                        </button>
                                        <span
                                            className="text-sm"
                                            style={{
                                                color: "var(--vl-text)",
                                            }}
                                        >
                                            {i.quantity}
                                        </span>
                                        <button
                                            onClick={() =>
                                                updateQty(
                                                    i.product_id,
                                                    i.quantity + 1,
                                                )
                                            }
                                            className="w-8 h-8 border flex items-center justify-center"
                                            style={{
                                                borderColor:
                                                    "var(--vl-border)",
                                            }}
                                        >
                                            <Plus size={12} />
                                        </button>
                                        <span
                                            className="ml-auto font-serif text-xl"
                                            style={{
                                                color: "var(--vl-text)",
                                            }}
                                        >
                                            {formatINR(
                                                i.price * i.quantity,
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <aside
                        className="lg:col-span-4 p-8 h-fit"
                        style={{
                            background: "var(--vl-surface)",
                            border: "1px solid var(--vl-border)",
                        }}
                    >
                        <div className="overline mb-6">Order summary</div>
                        <div className="flex justify-between py-2 text-sm">
                            <span style={{ color: "var(--vl-muted)" }}>
                                Subtotal
                            </span>
                            <span
                                data-testid="cart-page-subtotal"
                                style={{ color: "var(--vl-text)" }}
                            >
                                {formatINR(subtotal)}
                            </span>
                        </div>
                        <div className="flex justify-between py-2 text-sm">
                            <span style={{ color: "var(--vl-muted)" }}>
                                Shipping
                            </span>
                            <span style={{ color: "var(--vl-text)" }}>
                                {shipping === 0 ? "Complimentary" : formatINR(shipping)}
                            </span>
                        </div>
                        <div
                            className="hairline my-4"
                            style={{
                                background: "var(--vl-border)",
                                height: 1,
                            }}
                        />
                        <div className="flex justify-between items-baseline mt-4">
                            <span className="overline">Total</span>
                            <span
                                className="font-serif text-3xl"
                                style={{ color: "var(--vl-text)" }}
                                data-testid="cart-page-total"
                            >
                                {formatINR(total)}
                            </span>
                        </div>
                        <button
                            onClick={() => nav("/checkout")}
                            className="vl-btn vl-btn-solid w-full justify-center mt-8"
                            data-testid="cart-page-checkout"
                        >
                            Proceed to checkout
                        </button>
                        <p
                            className="text-[11px] mt-5 leading-relaxed"
                            style={{ color: "var(--vl-muted)" }}
                        >
                            Shipped in unbranded packaging. Return label reads{" "}
                            <em>V. Atelier</em>.
                        </p>
                    </aside>
                </div>
            )}
        </div>
    );
}
